// A waiting person's invite, in the People page's words
// (impire-hq 02-DESIGN/01-identity/05-invitations.md § the invite on the row,
// § the console and the terminal): one line beside the row, whether *Send
// again* applies, and the line *Add a person* answers with.

import { since, when } from "../../ui/components.tsx";
import { InviteLimit, InviteOutcome, InviteReason, type Invite, type Row } from "../../wire/auth.ts";

/** The words a limit is named by: whose daily limit it is. */
function whose(limit: string | undefined): string {
  switch (limit) {
    case InviteLimit.workspace:
      return "the workspace's";
    case InviteLimit.admin:
      return "your";
    case InviteLimit.address:
      return "this address's";
    default:
      return "a";
  }
}

/** reason is why an invite was not sent, as the page says it after "couldn't send the invite". */
export function reason(inv: Invite): string {
  switch (inv.reason) {
    case InviteReason.refused:
      return "the address was refused";
    case InviteReason.limit:
      return `${whose(inv.limit)} daily limit is reached until ${when(inv.frees)}`;
    case InviteReason.unavailable:
    default:
      return "the install sends no mail right now";
  }
}

/** NO_ADDRESS is what the page says of a pending row on a GitHub login. */
export const NO_ADDRESS = "No invite is sent for a GitHub login — tell them yourself";

/** NO_INVITE is what the page says of a waiting address whose row carries no invite yet. */
export const NO_INVITE = "No invite sent yet";

/** EMAIL is the handle claim an invite is sent for: an address (impire-sdk-go/identity: HandleClaimEmail). */
const EMAIL = "email";

/** InviteLine is the line beside a waiting person, and whether Send again applies. */
export interface InviteLine {
  text: string;
  sendAgain: boolean;
  /** first is a row with no invite yet: its button reads Send invite, not Send again. */
  first?: boolean;
}

/**
 * inviteLine is the line for a pending row's last invite; undefined for a
 * row that is not pending. A waiting address with no invite yet (one written
 * before invitations, or by a path that sent none) is offered Send invite; a
 * GitHub login with none is offered nothing (05-invitations.md § the invite
 * on the row, none yet).
 */
export function inviteLine(r: Row, now = Date.now()): InviteLine | undefined {
  const inv = r.invite;
  if (r.pending !== true) {
    return undefined;
  }
  if (inv === undefined) {
    return r.claim === EMAIL ? { text: NO_INVITE, sendAgain: true, first: true } : undefined;
  }
  if (inv.outcome === InviteOutcome.sent) {
    return { text: `Invite sent ${since(inv.at, now) || when(inv.at)}`, sendAgain: true };
  }
  if (inv.reason === InviteReason.noAddress) {
    return { text: NO_ADDRESS, sendAgain: false };
  }
  return {
    text: `Couldn't send the invite — ${reason(inv)}`,
    sendAgain: inv.reason !== InviteReason.limit,
  };
}

/** AddedLine is what Add a person says once the row is written, and the link to give where nothing was sent. */
export interface AddedLine {
  text: string;
  ok: boolean;
  /** link is the workspace's address, for the admin to pass on themselves. */
  link?: string;
}

/** addedLine is the one line Add a person answers with. */
export function addedLine(r: Row, handle: string, link: string): AddedLine {
  const inv = r.invite;
  if (inv === undefined) {
    return { text: `${handle} is added, and appears as waiting until their first sign-in.`, ok: true };
  }
  if (inv.outcome === InviteOutcome.sent) {
    return { text: `Invite sent to ${handle}.`, ok: true };
  }
  if (inv.reason === InviteReason.noAddress) {
    return { text: "Added — no invite is sent for a GitHub login; tell them yourself.", ok: true, link };
  }
  return { text: `Added — couldn't send the invite: ${reason(inv)}.`, ok: false, link };
}

/** workspaceLink is the console's address of the workspace: its sign-in hint. */
export function workspaceLink(origin: string, slug: string): string {
  return `${origin.replace(/\/+$/, "")}/${slug}`;
}
